import clsx from 'clsx';
import type { ReactNode } from 'react';
import { utcDateFormatter } from '~/components/settings/analytics/analytics-constants';

export const SectionHeading = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div>
    <h3 className="my-0 text-sm font-semibold text-neutral-900 dark:text-white">{title}</h3>
    {subtitle && (
      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{subtitle}</p>
    )}
  </div>
);

export const KpiTile = ({
  label,
  value,
  detail,
  truncate
}: {
  label: string;
  value: string;
  detail?: string;
  truncate?: boolean;
}) => (
  <div className="rounded-lg border border-neutral-200/70 dark:border-neutral-700/70 bg-neutral-50/40 dark:bg-neutral-900/40 px-4 py-3">
    <div className="text-[10px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
      {label}
    </div>
    <div
      className={`mt-1 text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white ${truncate ? 'truncate' : 'tabular-nums'}`}
      title={truncate ? value : undefined}
    >
      {value}
    </div>
    {detail ? (
      <div className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">{detail}</div>
    ) : null}
  </div>
);

export type TrendBar = {
  periodStart: Date;
  count: number;
  /** Shown on hover. */
  title: string;
  /** False draws a dashed placeholder for a period before recording started. */
  recorded: boolean;
};

/** One bar per period, scaled to the busiest, with the first and last dates under them. */
export const TrendBars = ({ bars, label }: { bars: TrendBar[]; label: string }) => {
  const max = Math.max(1, ...bars.map((bar) => bar.count));
  return (
    <>
      <div role="img" aria-label={label} className="flex h-20 items-end gap-1">
        {bars.map((bar) =>
          !bar.recorded ? (
            <div
              key={bar.periodStart.toISOString()}
              aria-hidden
              title={bar.title}
              className="h-2 flex-1 self-end rounded-t border border-dashed border-neutral-300 bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800"
            />
          ) : (
            <div
              key={bar.periodStart.toISOString()}
              aria-hidden
              title={bar.title}
              className="flex-1 rounded-t bg-primary-500/85 dark:bg-primary-400/85"
              style={{
                height:
                  bar.count === 0 ? '2px' : `${Math.max(4, Math.round((bar.count / max) * 100))}%`
              }}
            />
          )
        )}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-neutral-500 dark:text-neutral-400">
        <span>{utcDateFormatter.format(bars[0].periodStart)}</span>
        <span>{utcDateFormatter.format(bars[bars.length - 1].periodStart)}</span>
      </div>
    </>
  );
};

/** A deleted preset keeps its name, marked so nobody looks for it in the launcher. */
export function presetDisplayLabel(label: string, isDeleted: boolean) {
  return isDeleted ? `*${label}` : label;
}

/** Explains the * that presetDisplayLabel puts on a deleted preset's name. */
export const DeletedPresetFootnote = () => (
  <p className="mt-1 text-[11px] text-neutral-400 dark:text-neutral-500">
    * Preset has since been deleted. Its workspaces are still counted.
  </p>
);

export const EmptyMessage = ({ children }: { children: ReactNode }) => (
  <p className="py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">{children}</p>
);

/** Explains what the active filters leave out, in place of or beside the numbers. */
export const FilterNote = ({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) => (
  <p className={clsx('text-xs text-neutral-500 dark:text-neutral-400', className)}>{children}</p>
);
